import { useState, useMemo } from 'react';
import { Modal, Button } from '@components/ui';
import { useSelector } from 'react-redux';
import { getBlocksByConstructionId } from '@mocks/bloklar';
import { getApartmentsByBlockId } from '@mocks/daireler';
import { paymentTypeLabels } from '@mocks/daireler';

export default function QuickIncomeModal({ isOpen, onClose, onSubmit }) {
    const { constructions } = useSelector((state) => state.construction);

    const [step, setStep] = useState(1);
    const [selectedConstruction, setSelectedConstruction] = useState(null);
    const [selectedBlock, setSelectedBlock] = useState(null);
    const [selectedApartment, setSelectedApartment] = useState(null);

    // Form Data
    const [amount, setAmount] = useState('');
    const [paymentType, setPaymentType] = useState('taksit');
    const [date, setDate] = useState(new Date().toISOString().split('T')[0]);
    const [description, setDescription] = useState('');

    // New Customer Data
    const [newCustomerName, setNewCustomerName] = useState('');
    const [newCustomerPhone, setNewCustomerPhone] = useState('');

    // Reset when closed
    const handleClose = () => {
        setStep(1);
        setSelectedConstruction(null);
        setSelectedBlock(null);
        setSelectedApartment(null);
        setAmount('');
        setDescription('');
        setNewCustomerName('');
        setNewCustomerPhone('');
        onClose();
    };

    // Cascading Data
    const blocks = useMemo(() =>
        selectedConstruction ? getBlocksByConstructionId(selectedConstruction.id) : [],
        [selectedConstruction]
    );

    const apartments = useMemo(() =>
        selectedBlock ? getApartmentsByBlockId(selectedBlock.id) : [],
        [selectedBlock]
    );

    const handleSubmit = (e) => {
        e.preventDefault();
        onSubmit({
            type: 'income',
            construction: selectedConstruction,
            block: selectedBlock,
            apartment: selectedApartment,
            amount: parseFloat(amount),
            paymentType,
            date,
            description: description || `${selectedApartment.number} nolu daire ödemesi`,
            newCustomer: selectedApartment.status === 'available' ? {
                name: newCustomerName,
                phone: newCustomerPhone
            } : null
        });
        handleClose();
    };

    return (
        <Modal
            isOpen={isOpen}
            onClose={handleClose}
            title="Hızlı Gelir Girişi"
            size="md"
        >
            <form onSubmit={handleSubmit} className="space-y-4">

                {/* 1. Adım: Proje Seçimi */}
                {step === 1 && (
                    <div className="space-y-2">
                        <label className="block text-sm font-medium text-text-secondary">Proje Seçin</label>
                        <div className="grid grid-cols-1 gap-2 max-h-60 overflow-y-auto">
                            {constructions.map(c => (
                                <button
                                    type="button"
                                    key={c.id}
                                    onClick={() => {
                                        setSelectedConstruction(c);
                                        setStep(2);
                                    }}
                                    className="p-3 text-left rounded-lg border border-border-light hover:border-primary hover:bg-primary/5 transition-colors flex justify-between items-center"
                                >
                                    <span className="font-medium text-text-primary">{c.name}</span>
                                    <span className="text-xs text-text-muted">Seç &rarr;</span>
                                </button>
                            ))}
                        </div>
                    </div>
                )}

                {/* 2. Adım: Blok ve Daire Seçimi */}
                {step === 2 && (
                    <div className="space-y-4 animate-in slide-in-from-right-4 duration-300">
                        <div className="flex items-center justify-between">
                            <h3 className="text-sm font-semibold text-primary">{selectedConstruction.name}</h3>
                            <button type="button" onClick={() => setStep(1)} className="text-xs text-text-muted hover:text-text-primary">Değiştir</button>
                        </div>

                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <label className="block text-xs font-medium text-text-secondary mb-1">Blok</label>
                                <select
                                    className="w-full p-2 rounded-lg border border-border-light bg-bg-primary text-sm"
                                    onChange={(e) => {
                                        const block = blocks.find(b => b.id === e.target.value);
                                        setSelectedBlock(block);
                                        setSelectedApartment(null);
                                    }}
                                    value={selectedBlock?.id || ''}
                                >
                                    <option value="">Seçiniz</option>
                                    {blocks.map(b => (
                                        <option key={b.id} value={b.id}>{b.name}</option>
                                    ))}
                                </select>
                            </div>
                            <div>
                                <label className="block text-xs font-medium text-text-secondary mb-1">Daire</label>
                                <select
                                    className="w-full p-2 rounded-lg border border-border-light bg-bg-primary text-sm"
                                    disabled={!selectedBlock}
                                    onChange={(e) => {
                                        const apt = apartments.find(a => a.id === e.target.value);
                                        setSelectedApartment(apt);
                                    }}
                                    value={selectedApartment?.id || ''}
                                >
                                    <option value="">Seçiniz</option>
                                    {apartments.map(a => (
                                        <option key={a.id} value={a.id}>
                                            {a.number} {a.buyerName ? `(${a.buyerName})` : '(Boş)'}
                                        </option>
                                    ))}
                                </select>
                            </div>
                        </div>

                        {selectedApartment && (
                            <div className="space-y-3">
                                <div className="p-3 bg-bg-tertiary/50 rounded-lg text-sm border border-border-light">
                                    <div className="flex justify-between mb-1">
                                        <span className="text-text-muted">Mevcut Durum:</span>
                                        <span className={`font-medium ${selectedApartment.status === 'available' ? 'text-success' : 'text-text-primary'}`}>
                                            {selectedApartment.status === 'available' ? 'Boş (Satışa Hazır)' : 'Satıldı'}
                                        </span>
                                    </div>
                                    <div className="flex justify-between mb-1">
                                        <span className="text-text-muted">Alıcı:</span>
                                        <span className="font-medium text-text-primary">{selectedApartment.buyerName || '-'}</span>
                                    </div>
                                    {selectedApartment.status !== 'available' && (
                                        <div className="flex justify-between">
                                            <span className="text-text-muted">Kalan Borç:</span>
                                            <span className="font-bold text-warning">
                                                {new Intl.NumberFormat('tr-TR', { style: 'currency', currency: 'TRY' }).format(selectedApartment.remainingAmount)}
                                            </span>
                                        </div>
                                    )}
                                </div>

                                {/* Yeni Müşteri Ekleme Formu (Eğer Daire Boşsa) */}
                                {selectedApartment.status === 'available' && (
                                    <div className="bg-primary/5 border border-primary/20 rounded-lg p-3 space-y-3">
                                        <div className="text-xs font-bold text-primary uppercase tracking-wide">Yeni Müşteri Bilgileri</div>
                                        <div>
                                            <input
                                                type="text"
                                                placeholder="Ad Soyad"
                                                className="w-full p-2 rounded border border-border-light text-sm"
                                                // Bu kısmı state ile bağlamak gerek, QuickIncomeModal'a state ekleyeceğiz
                                                onChange={(e) => window.newCustomerName = e.target.value}
                                            />
                                        </div>
                                        <div>
                                            <input
                                                type="text"
                                                placeholder="Telefon"
                                                className="w-full p-2 rounded border border-border-light text-sm"
                                                onChange={(e) => window.newCustomerPhone = e.target.value}
                                            />
                                        </div>
                                        <div className="text-[10px] text-text-muted leading-tight">
                                            * Hızlı satış işlemi için sadece temel bilgiler yeterlidir. Detayları daha sonra müşteri kartından düzenleyebilirsiniz.
                                        </div>
                                    </div>
                                )}

                                <Button type="button" onClick={() => setStep(3)} className="w-full" variant="primary">
                                    {selectedApartment.status === 'available' ? 'Satışı Başlat & Ödeme Al' : 'Ödeme Almaya Devam Et'}
                                </Button>
                            </div>
                        )}
                    </div>
                )}

                {/* 3. Adım: Tutar ve Detay */}
                {step === 3 && (
                    <div className="space-y-4 animate-in slide-in-from-right-4 duration-300">
                        <div className="flex items-center justify-between pb-2 border-b border-border-light">
                            <div>
                                <div className="text-xs text-text-muted">{selectedConstruction.name} / {selectedBlock.name}</div>
                                <div className="font-bold text-text-primary">Daire {selectedApartment.number}</div>
                            </div>
                            <button type="button" onClick={() => setStep(2)} className="text-xs text-text-muted hover:text-text-primary">Geri</button>
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-text-secondary mb-1">Tutar (TL)</label>
                            <input
                                type="number"
                                required
                                value={amount}
                                onChange={(e) => setAmount(e.target.value)}
                                className="w-full p-3 text-lg font-bold rounded-lg border border-border-light focus:border-success focus:ring-1 focus:ring-success outline-none"
                                placeholder="0.00"
                            />
                        </div>

                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <label className="block text-sm font-medium text-text-secondary mb-1">Ödeme Türü</label>
                                <select
                                    value={paymentType}
                                    onChange={(e) => setPaymentType(e.target.value)}
                                    className="w-full p-2.5 rounded-lg border border-border-light bg-bg-primary text-sm"
                                >
                                    {Object.entries(paymentTypeLabels).map(([key, label]) => (
                                        <option key={key} value={key}>{label}</option>
                                    ))}
                                </select>
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-text-secondary mb-1">Tarih</label>
                                <input
                                    type="date"
                                    required
                                    value={date}
                                    onChange={(e) => setDate(e.target.value)}
                                    className="w-full p-2.5 rounded-lg border border-border-light bg-bg-primary text-sm"
                                />
                            </div>
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-text-secondary mb-1">Açıklama</label>
                            <input
                                type="text"
                                value={description}
                                onChange={(e) => setDescription(e.target.value)}
                                className="w-full p-2.5 rounded-lg border border-border-light bg-bg-primary text-sm"
                                placeholder="Opsiyonel açıklama..."
                            />
                        </div>

                        <div className="pt-2 flex gap-3">
                            <Button type="button" variant="outline" onClick={handleClose} className="flex-1">İptal</Button>
                            <Button type="submit" variant="success" className="flex-[2]">Geliri Kaydet</Button>
                        </div>
                    </div>
                )}
            </form>
        </Modal>
    );
}
